/**
 * CategoryExplainTooltip Component
 * Phase 1: Smart Transaction Engine 2.0
 * 
 * Shows AI explanation for why a category was chosen,
 * with alternative suggestions.
 */

import React, { useState, useEffect } from 'react';
import { Sparkles, Info, ChevronRight, Loader2, Lightbulb, ArrowRight } from 'lucide-react';
import { explainCategory } from '@/services/aiService';

/**
 * Renders a confidence badge with appropriate styling
 */
const ConfidenceBadge = ({ confidence, label }) => {
  const getColor = () => {
    if (confidence >= 0.85) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    if (confidence >= 0.70) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
    if (confidence >= 0.60) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
    return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getColor()}`}>
      {label || `${Math.round(confidence * 100)}%`}
    </span>
  );
};

/**
 * Main CategoryExplainTooltip component
 */
const CategoryExplainTooltip = ({
  description,
  categoryId,
  categoryName,
  confidence = 0.8,
  amount,
  onSelectAlternative,
  compact = false
}) => {
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch explanation when component mounts or key props change
  useEffect(() => {
    const fetchExplanation = async () => {
      if (!description || !categoryId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const result = await explainCategory({
          description,
          categoryId,
          confidence,
          amount
        });
        
        if (result?.explanation) {
          setExplanation(result.explanation);
        }
      } catch (err) {
        console.error('Failed to get category explanation:', err);
        setError('Could not load explanation');
      } finally {
        setLoading(false);
      }
    };

    fetchExplanation();
  }, [description, categoryId, confidence, amount]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-violet-600 dark:text-violet-400">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Analyzing...</span>
      </div>
    );
  }

  if (error || !explanation) {
    return null; // Silent fail - don't break the UI
  }

  // Compact mode - just show a small info icon with tooltip content
  if (compact) {
    return (
      <div className="group relative inline-block">
        <button 
          type="button"
          className="p-1 rounded-full hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors"
          aria-label="Why this category?"
        >
          <Lightbulb className="h-3.5 w-3.5 text-violet-500" />
        </button>
        
        {/* Tooltip on hover */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-violet-200 dark:border-violet-800 p-3 w-64">
            <div className="text-xs text-gray-700 dark:text-gray-300 mb-2">
              {explanation.explainerText}
            </div>
            {explanation.matchedKeywords?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {explanation.matchedKeywords.slice(0, 3).map((kw, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 rounded text-xs">
                    {kw}
                  </span>
                ))}
              </div>
            )}
            {explanation.alternatives?.length > 0 && onSelectAlternative && (
              <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-2 mt-2">
                {explanation.switchPrompt}
              </div>
            )}
            {/* Arrow pointer */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white dark:border-t-gray-800" />
          </div>
        </div>
      </div>
    );
  }

  // Full mode - expandable panel
  return (
    <div className="mt-2 rounded-lg border border-violet-100 dark:border-violet-900 bg-gradient-to-br from-violet-50/50 to-white dark:from-violet-900/20 dark:to-gray-800 overflow-hidden">
      {/* Header - always visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-violet-50/50 dark:hover:bg-violet-900/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-violet-500" />
          <span className="text-sm text-violet-700 dark:text-violet-300 font-medium">
            Why {categoryName}?
          </span>
          <ConfidenceBadge confidence={confidence} label={explanation.confidenceLabel} />
        </div>
        <ChevronRight 
          className={`h-4 w-4 text-violet-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-violet-100 dark:border-violet-900">
          {/* Explanation text */}
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
            {explanation.explainerText}
          </p>

          {/* Matched keywords */}
          {explanation.matchedKeywords?.length > 0 && (
            <div className="mt-2">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Detected signals:</div>
              <div className="flex flex-wrap gap-1">
                {explanation.matchedKeywords.map((kw, i) => (
                  <span 
                    key={i} 
                    className="px-2 py-0.5 bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 rounded-full text-xs"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Alternatives */}
          {explanation.alternatives?.length > 0 && onSelectAlternative && (
            <div className="mt-3 pt-2 border-t border-violet-100 dark:border-violet-900">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Not quite right? Try:
              </div>
              <div className="flex flex-wrap gap-2">
                {explanation.alternatives.map((alt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => onSelectAlternative(alt.categoryId)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-violet-50 dark:hover:bg-violet-900/30 hover:border-violet-300 dark:hover:border-violet-700 transition-colors"
                  >
                    <ArrowRight className="h-3 w-3 text-gray-400" />
                    {alt.categoryName}
                    {alt.matchedKeywords?.length > 0 && (
                      <span className="text-gray-400">
                        ({alt.matchedKeywords[0]})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryExplainTooltip;
